import React, { useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import ReCAPTCHA from 'react-google-recaptcha';
import {
    Box,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
    InputLabel,
    Select,
    OutlinedInput,
    MenuItem,
    FormControl
} from '@mui/material';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import ApiHelper from 'utils/apiHelper';
import { validateEmail, validateUsername } from 'utils/commonHelpers';
import config from 'config';

const EditUserDialog = ({ onClose, data, userGroupsData, limitFields }) => {
    const [loading, setLoading] = useState(false);
    const [username, setUsername] = useState(data.username);
    const [email, setEmail] = useState(data.email);
    const [locked, setLocked] = useState(data.locked);
    const [resetPassword, setResetPassword] = useState(false);
    const [notificationEmail, setNotificationEmail] = useState(true);
    const [userGroupId, setUserGroupId] = useState(data.user_group_ids);

    const handleLockAccount = (e) => {
        setLocked(e.target.checked);
    };

    const handleChangeUserGroupId = (e) => {
        setUserGroupId(e.target.value);
    };

    const handleChangeUserName = (e) => {
        if (e.target.value.length > limitFields['username']) {
            toast.warning('This field must be ' + limitFields['username'] + ' characters or less.');
        } else {
            setUsername(e.target.value);
        }
    };

    const handleChangeEmail = (e) => {
        if (e.target.value.length > limitFields['email']) {
            toast.warning('This field must be ' + limitFields['email'] + ' characters or less.');
        } else {
            setEmail(e.target.value);
        }
    };

    const handleSubmit = () => {
        if (username === '') {
            toast.info('Username is required.');
            return true;
        } else {
            if (!validateUsername(username)) {
                toast.info('The username may contain letters, numbers, and _ . - characters');
                return true;
            }
            if (email === '') {
                toast.info('Email is required.');
                return true;
            }
            if (!validateEmail(email)) {
                toast.info('Invalid email format.');
                return true;
            }
        }
        if (userGroupId.length === 0) {
            toast.info('Please select the user group');
            return true;
        }
        setLoading(true);

        ApiHelper.put(`/api/management/users/${data.id}/`, {
            username: username,
            email: email,
            locked: locked,
            reset_password: resetPassword,
            send_notification_email: notificationEmail,
            user_group_names: userGroupId
        })
            .then(() => {
                toast.success('Successfully updated.');
                setLoading(false);
                onClose();
            })
            .catch(() => {
                toast.error('Failed to add a new user.');
                setLoading(false);
            });
    };

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle sx={{ fontSize: '16px' }}>Edit User</DialogTitle>
            <DialogContent sx={{ minWidth: '500px' }}>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{
                            width: '100%'
                        }}
                        label="Username"
                        value={username}
                        onChange={(e) => handleChangeUserName(e)}
                    />
                </Box>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{
                            width: '100%'
                        }}
                        label="Email"
                        value={email}
                        onChange={(e) => handleChangeEmail(e)}
                    />
                </Box>
                <Box sx={{ mt: 3 }}>
                    <FormControl sx={{ width: '100%' }}>
                        <InputLabel>Select the user group</InputLabel>
                        <Select
                            displayEmpty
                            value={userGroupId}
                            name="userGroupId"
                            multiple
                            input={<OutlinedInput label="Select the user group" />}
                            onChange={handleChangeUserGroupId}
                            renderValue={(selected) => selected.join(', ')}
                        >
                            {userGroupsData.map((data, key) => (
                                <MenuItem value={data.name} key={key}>
                                    <Checkbox checked={userGroupId.indexOf(data.name) > -1} />
                                    {data.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ mt: 3 }}>
                    <FormControlLabel
                        control={<Checkbox checked={locked} onChange={handleLockAccount} color="primary" />}
                        label="Lock User"
                        sx={{ width: '45%' }}
                    />
                    <FormControlLabel
                        control={<Checkbox checked={resetPassword} onChange={(e) => setResetPassword(e.target.checked)} color="primary" />}
                        label="Reset Password"
                        sx={{ width: '45%' }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={notificationEmail}
                                onChange={(e) => setNotificationEmail(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Send Notification Email"
                    />
                </Box>
                <Box sx={{ mt: 3, mb: 3 }}>
                    <ReCAPTCHA sitekey={config.recaptchaSiteKey} onChange={() => setIsCaptchaSuccess(true)} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <LoadingButton onClick={handleSubmit} loading={loading} loadingIndicator="Saving…" variant="outlined">
                    <span>Submit</span>
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

EditUserDialog.propTypes = {
    onClose: PropTypes.func,
    data: PropTypes.object,
    userGroupsData: PropTypes.array,
    limitFields: PropTypes.object
};

export default EditUserDialog;
